export interface FormatHintMeta {
  description: string;
  example: string;
}

export interface VariableMeta {
  label: string;
  description: string;
}

export interface NamingConfig {
  variables: {
    insight: Record<string, VariableMeta>;
    db: Record<string, VariableMeta>;
  };
  format_hints: Record<string, Record<string, FormatHintMeta>>;
}

import {
  buildNamingTemplatePreview,
  createNamingTemplateToken,
  parseNamingTemplate,
  serializeNamingTemplate,
  type NamingTemplateToken,
} from '@liscio/common';

export type TemplateToken = NamingTemplateToken;

export const createToken = createNamingTemplateToken;

export interface FirmTemplate {
  document_type: string;
  template_string: string | null;
  is_firm_override: boolean;
  origin: string | null;
  label: string;
}

// Preferred display order for variable chips
export const VARIABLE_ORDER = [
  'extended_type', 'year', 'month', 'issuer',
  'account_name', 'contact_name',
];

// Hidden from the UI for initial rollout — enable when per-type templates are supported
export const SUPPRESSED_VARIABLES = new Set([
  'doc_type', 'subtype', 'upload_year', 'upload_month',
]);

// Suggested starter templates — createToken called at import time for stable IDs
export const SUGGESTED_TEMPLATES: TemplateToken[][] = [
  [createToken('year'), createToken('extended_type'), createToken('issuer')],
  [createToken('account_name'), createToken('extended_type'), createToken('year')],
  [createToken('contact_name', 'initials'), createToken('extended_type'), createToken('issuer'), createToken('year')],
  [createToken('year'), createToken('month', 'Mon'), createToken('extended_type'), createToken('account_name', 'first')],
];

export const FIRM_DEFAULT_TYPE = '__all__';

// --- Separator support ---

export const SEPARATOR_OPTIONS = [
  { value: ' ', label: 'Space', display: ' ' },
  { value: '-', label: 'Dash', display: '–' },
  { value: '_', label: 'Underscore', display: '_' },
  { value: '.', label: 'Period', display: '.' },
] as const;

export const DEFAULT_SEPARATOR = ' ';

export interface ParsedTemplate {
  tokens: TemplateToken[];
  separators: string[]; // length = max(0, tokens.length - 1)
  prefix: string;
  suffix: string;
}

export const parseTemplateString = parseNamingTemplate;

export const serializeTokens = serializeNamingTemplate;

export const buildPreview = (
  tokens: TemplateToken[],
  separators: string[],
  documentValues?: Record<string, string> | null,
  prefix = '',
  suffix = '',
): string => {
  return buildNamingTemplatePreview(
    tokens,
    separators,
    documentValues,
    prefix,
    suffix,
  );
};
